import React from 'react';
import { FiMessageSquare } from 'react-icons/fi';
import {
  metaAccountStateLabel,
  type MetaAccountStatus,
  type MetaConnectionStatus,
  type WhatsAppCompleteResult,
} from '../../services/MetaConnectionService';
import WhatsAppSignupButton from './WhatsAppSignupButton';

export interface WhatsAppCloudCardProps {
  sectorId: number;
  /** undefined = ainda consultando · null = a consulta falhou */
  status: MetaConnectionStatus | null | undefined;
  /** O setor ainda tem linha Spark: o botão vira "Migrar para a conexão direta com a Meta". */
  hasSpark: boolean;
  disconnecting: boolean;
  onConnected: (result: WhatsAppCompleteResult) => void;
  onDisconnect: () => void;
  onError: (message: string) => void;
}

/** O WhatsApp oficial está configurado neste ambiente (e o servidor mandou o que o navegador precisa)? */
export const isWhatsAppCloudConfigured = (status: MetaConnectionStatus | null | undefined): boolean =>
  !!status && (status.whatsApp?.configured ?? false) && !!status.whatsAppSignup;

const QUALITY_LABELS: Record<string, string> = {
  GREEN: 'Alta',
  YELLOW: 'Média',
  RED: 'Baixa',
  UNKNOWN: 'sem avaliação ainda',
};

/** A sincronização da coexistência tem prazo de 24 h depois do cadastro. */
const SYNC_WINDOW_MS = 24 * 60 * 60 * 1000;

const syncNote = (a: MetaAccountStatus): string | null => {
  if (!a.isOnBizApp) return null;
  if (a.historySyncStatus === 'failed')
    return 'Não foi possível pedir a importação das conversas. As mensagens novas chegam normalmente.';
  // A empresa recusou compartilhar o histórico no celular (erro 2593109): o texto vem pronto do servidor.
  if (a.historySyncStatus === 'declined')
    return `${a.historySyncMessage || 'O histórico não foi compartilhado no celular'}. As mensagens novas chegam normalmente.`;
  if (a.historySyncStatus === 'completed') return 'Conversas dos últimos 6 meses importadas.';
  // Os lotes chegam por webhook e podem levar horas: com andamento, mostra a porcentagem sem o limite de 24 h.
  if (a.historySyncStatus === 'importing')
    return typeof a.historySyncProgress === 'number'
      ? `Importando conversas… ${a.historySyncProgress}%. Mantenha o WhatsApp Business aberto no celular.`
      : 'Importando conversas dos últimos 6 meses… mantenha o WhatsApp Business aberto no celular.';
  if (a.historySyncStatus === 'requested' && a.historySyncRequestedAt) {
    const at = new Date(a.historySyncRequestedAt).getTime();
    if (!isNaN(at) && Date.now() - at < SYNC_WINDOW_MS)
      return 'Importando conversas dos últimos 6 meses… mantenha o WhatsApp Business aberto no celular.';
  }
  return null;
};

const isProblem = (a: { status: string; failedEvents: number; tokenExpiryWarning?: string | null }) =>
  a.status !== 'active' || a.failedEvents > 0 || !!a.tokenExpiryWarning;

/**
 * Cartão do WhatsApp oficial (Cloud API direta da Meta) em Configurações › Conexões e no Onboarding. Setor sem conta
 * mostra "Conectar WhatsApp oficial (Meta)"; setor que ainda tem Spark mostra "Migrar para a conexão direta com a Meta". Com
 * conta, mostra número, nome verificado, qualidade, coexistência, aviso de pagamento e o andamento da importação.
 */
const WhatsAppCloudCard: React.FC<WhatsAppCloudCardProps> = ({
  sectorId,
  status,
  hasSpark,
  disconnecting,
  onConnected,
  onDisconnect,
  onError,
}) => {
  if (!status || !isWhatsAppCloudConfigured(status) || !status.whatsAppSignup) return null;
  const config = status.whatsAppSignup;
  const accounts = status.accounts.filter(a => a.channel === 'whatsapp');
  const hasAccounts = accounts.length > 0;
  const hasProblem = accounts.some(isProblem);
  const statusColor = hasAccounts && !hasProblem ? 'var(--success-color)' : 'var(--error-color)';
  const statusLabel = !hasAccounts ? 'Desconectado' : hasProblem ? 'Precisa de atenção' : 'Online';

  return (
    <div className="sector-channel whatsapp-cloud-channel">
      <div className="sector-channel-head">
        <h4><FiMessageSquare size={16} /> WhatsApp oficial (Meta)</h4>
        <div className="whatsapp-status" style={{ color: statusColor }}>
          <span className="status-dot" style={{ backgroundColor: statusColor }} />
          <span>{statusLabel}</span>
        </div>
      </div>
      {hasAccounts ? (
        <ul className="meta-account-list">
          {accounts.map(a => {
            const problem = isProblem(a);
            const note = syncNote(a);
            const quality = a.qualityRating ? QUALITY_LABELS[a.qualityRating] ?? null : null;
            return (
              <li key={a.accountId} className="meta-account-row">
                <span className="meta-account-name">{a.displayPhone || a.accountId}</span>
                {a.name ? <span className="meta-account-token">{a.name}</span> : null}
                <span className="meta-account-state" style={{ color: problem ? 'var(--error-color)' : 'var(--success-color)' }}>
                  {metaAccountStateLabel(a.status)}
                </span>
                {quality ? <span className="meta-account-token">{`Qualidade: ${quality}`}</span> : null}
                {a.isOnBizApp ? (
                  <>
                    <span className="meta-account-token">Coexistência: o número também está no WhatsApp Business app.</span>
                    <span className="meta-account-token">
                      Para desligar de vez a API, abra o WhatsApp Business no celular: Configurações, Conta, Plataforma Business.
                    </span>
                  </>
                ) : null}
                {note ? <span className="meta-account-token" role="status">{note}</span> : null}
                {a.tokenExpiryWarning ? <span className="meta-account-error" role="alert">{a.tokenExpiryWarning}</span> : null}
                {a.paymentWarning ? <span className="meta-account-error" role="alert">{a.paymentWarning}</span> : null}
                {a.lastError && problem ? <span className="meta-account-error">{a.lastError}</span> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {!hasAccounts && hasSpark ? (
        <p className="sector-description">
          Ao concluir, a conexão anterior do WhatsApp deste setor é desligada e tudo passa a ir direto pela Meta.
        </p>
      ) : null}
      <div className="whatsapp-buttons-container">
        {!hasAccounts ? (
          <WhatsAppSignupButton
            sectorId={sectorId}
            config={config}
            label={hasSpark ? 'Migrar para a conexão direta com a Meta' : 'Conectar WhatsApp oficial (Meta)'}
            onConnected={onConnected}
            onError={onError}
          />
        ) : (
          <>
            {hasProblem ? (
              <WhatsAppSignupButton
                sectorId={sectorId}
                config={config}
                label="Reconectar WhatsApp"
                onConnected={onConnected}
                onError={onError}
              />
            ) : null}
            <button type="button" className="whatsapp-connect-button is-danger" onClick={onDisconnect} disabled={disconnecting}>
              {disconnecting ? 'Desconectando…' : 'Desconectar WhatsApp'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default WhatsAppCloudCard;
